// Order / ladder windows opened by the widgets' buttons (lightning = ladder,
// plus = limit order, pencil = modify, arrows = reverse a position).

import { ChartParams, ContractInfo, OrderInfo, Widget } from "./types";

export const DEFAULT_STOCK_SIZE = 50;
export const DEFAULT_OPTION_SIZE = 10;

export interface WidgetActions {
  open: (text: string, widget: Omit<Widget, "id">, options?: { exists?: (w: Widget) => boolean }) => void;
  // closes every window of the chat matching `match` (e.g. all windows of a cancelled order)
  closeWhere: (match: (w: Widget) => boolean) => void;
  patch: (params: Record<string, any>) => void;
  duplicate: (params: Record<string, any>) => void;
  close: () => void;
  // a chart card's Open: the live chart in its own (Dockview) window
  openChart: (params: ChartParams, title: string) => void;
}

export interface WidgetProps {
  widget: Widget;
  actions: WidgetActions;
  // lists opened with the footer buttons: the message's timestamp line; the
  // widget then renders the message header itself, with a live intro ("3 open positions.")
  intro?: string;
}

// reference price of a quote (backend: IBKRFast.default_limit_price): last when
// it lies within the bid-ask, otherwise the mid; with one side missing the
// last, and without a last whichever of bid / ask exists
export function referencePrice(q: { bid: number | null; last: number | null; ask: number | null } | undefined) {
  if (!q) return null;
  const { bid, last, ask } = q;
  if (bid != null && ask != null) return last != null && last >= bid && last <= ask ? last : Math.round(((bid + ask) / 2) * 10000) / 10000;
  return last ?? bid ?? ask;
}

export const defaultSize = (c: ContractInfo) => (c.secType === "OPT" ? DEFAULT_OPTION_SIZE : DEFAULT_STOCK_SIZE);

export function openLadder(actions: WidgetActions, contract: ContractInfo, size = defaultSize(contract), side?: "BUY" | "SELL") {
  const text = side
    ? `Ladder ${side.toLowerCase()}, ${size} ${contract.label}.`
    : `Ladder buy / sell, ${contract.label}. Set the size, then pick a side.`;
  actions.open(text, { type: "ladder", params: { conId: contract.conId, size, side: side ?? null } });
}

// limit windows start at size 1 (the + buttons; reverse passes the position size)
export function openLimit(actions: WidgetActions, contract: ContractInfo, price: number | null, side: "BUY" | "SELL" = "BUY", size = 1, text?: string) {
  actions.open(text ?? `New limit order, ${contract.label}. Press Submit to send it.`, {
    type: "ticket",
    params: { conId: contract.conId, side, size, price: price ?? 0.01 },
  });
}

const isModifyOf = (orderId: number) => (w: Widget) => w.type === "ticket" && w.params.variant === "modify" && w.params.orderId === orderId;

// one modify window per order: the pencil doesn't open a second one
export function openModify(actions: WidgetActions, order: OrderInfo) {
  actions.open("Modify order. Changes apply right away.",
    { type: "ticket", params: { orderId: order.orderId, conId: order.contract.conId, variant: "modify" } },
    { exists: isModifyOf(order.orderId) });
}

// a cancelled order's modify windows go away with it
export const closeModifyWindows = (actions: WidgetActions, orderId: number) => actions.closeWhere(isModifyOf(orderId));
